import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dropdown,
  ListItemDecorator,
  Menu,
  MenuButton,
  MenuItem,
  Modal,
  ModalDialog,
  Stack,
} from '@mui/joy';
import PaymentIcon from '@mui/icons-material/Payment';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { OmrAmount } from '../../../main/components/OmrAmount';
import { payCustomerInvoiceCheckout, payFullCustomerInvoice } from '../../../main/API/customer';
import {
  getCheckoutApiErrorMessage,
  getCheckoutTitle,
  getPayableCheckouts,
  getPrimaryPaymentLink,
  isInvoiceFullyPaid,
} from '../../../main/utils/invoiceCheckouts';
import { getDocLocale } from '../../../shipments_manager/views/htmlDocumentUtils';

/**
 * Customer Pay actions for an invoice.
 * Pay always requests a fresh gateway link from the server before opening checkout.
 */
export default function CustomerInvoicePayActions({
  invoice,
  needsRegenerate,
  onRegenerate,
  regenerating = false,
  onAfterPayFull,
  size = 'sm',
  fullWidth = false,
  variant = 'soft',
  showStartDecorator = true,
  compact = false,
  iconOnly = false,
  hidePayFullWhenSingleLink = false,
}) {
  const { t, i18n } = useTranslation('ui');
  const locale = getDocLocale(i18n.language);
  const [payFullOpen, setPayFullOpen] = useState(false);
  const [payFullLoading, setPayFullLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [error, setError] = useState('');

  if (!invoice || isInvoiceFullyPaid(invoice)) {
    return null;
  }

  const payable = getPayableCheckouts(invoice);
  const amountDue = Number(invoice.amount_due != null ? invoice.amount_due : 0);
  const canPayFull = amountDue > 0;
  const mustRegenerate = typeof needsRegenerate === 'function' && needsRegenerate(invoice);
  const primaryLink = getPrimaryPaymentLink(invoice);
  const singleCheckoutId = payable.length === 1 ? payable[0].id : undefined;
  const showSinglePay = payable.length <= 1 && (payable.length === 1 || primaryLink || mustRegenerate);
  const singlePayLinkOnly = payable.length <= 1 && (payable.length === 1 || !!primaryLink);
  const showPayFullRemaining = canPayFull && !(hidePayFullWhenSingleLink && singlePayLinkOnly);

  const openPaymentLink = (link) => {
    if (link) {
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  };

  const handlePayCheckout = async (checkoutId = undefined) => {
    setError('');
    setPayLoading(true);
    try {
      const response = await payCustomerInvoiceCheckout(invoice.id, checkoutId);
      const link =
        (response && response.data && response.data.payment_link)
        || (response && response.data && response.data.checkout && response.data.checkout.payment_link);
      openPaymentLink(link);
      if (typeof onAfterPayFull === 'function') {
        await onAfterPayFull(response);
      }
    } catch (err) {
      setError(getCheckoutApiErrorMessage(err, t('customer.paymentLinkFailed')));
    } finally {
      setPayLoading(false);
    }
  };

  const handlePayFull = async () => {
    setError('');
    setPayFullLoading(true);
    try {
      const response = await payFullCustomerInvoice(invoice.id);
      const link =
        (response && response.data && response.data.payment_link)
        || (response && response.data && response.data.checkout && response.data.checkout.payment_link);
      setPayFullOpen(false);
      openPaymentLink(link);
      if (typeof onAfterPayFull === 'function') {
        await onAfterPayFull(response);
      }
    } catch (err) {
      setError(getCheckoutApiErrorMessage(err, t('customer.payFullFailed')));
      setPayFullOpen(false);
    } finally {
      setPayFullLoading(false);
    }
  };

  const compactPayBtnSx = {
    borderRadius: '8px',
    fontWeight: 500,
    fontSize: '0.8125rem',
    minHeight: 32,
    py: 0.25,
    px: 1,
    whiteSpace: 'nowrap',
  };

  const renderCompactPayButton = (onClick, { loading = false, disabled = false } = {}) => (
    <Button
      variant="soft"
      color="primary"
      size="sm"
      onClick={onClick}
      loading={loading}
      disabled={disabled}
      startDecorator={<PaymentIcon sx={{ fontSize: 16 }} />}
      sx={compactPayBtnSx}
    >
      {t('customer.pay')}
    </Button>
  );

  const renderPayButton = (onClick, { loading = false, disabled = false, label = t('customer.pay') } = {}) => (
    <Button
      variant={variant}
      color="primary"
      size={size}
      fullWidth={fullWidth}
      onClick={onClick}
      loading={loading}
      disabled={disabled}
      startDecorator={showStartDecorator ? <PaymentIcon /> : undefined}
      endDecorator={!showStartDecorator ? <OpenInNewIcon sx={{ fontSize: 16 }} /> : undefined}
      sx={{ borderRadius: compact ? '8px' : '10px', fontWeight: 500 }}
    >
      {label}
    </Button>
  );

  const payFullButton = showPayFullRemaining ? (
    iconOnly ? (
      renderCompactPayButton(() => setPayFullOpen(true))
    ) : (
      <Button
        variant="outlined"
        color="primary"
        size={size}
        fullWidth={fullWidth}
        startDecorator={showStartDecorator ? <PaymentIcon /> : undefined}
        endDecorator={!showStartDecorator ? <OpenInNewIcon sx={{ fontSize: 16 }} /> : undefined}
        onClick={() => setPayFullOpen(true)}
        sx={{ borderRadius: compact ? '8px' : '10px', fontWeight: 500 }}
      >
        {t('customer.payFullRemaining')}
        {invoice.amount_due != null ? (
          <>
            {' '}(<OmrAmount value={invoice.amount_due} />)
          </>
        ) : null}
      </Button>
    )
  ) : null;

  const renderPayMenu = () => (
    <Dropdown>
      <MenuButton
        variant="soft"
        color="primary"
        size="sm"
        loading={payLoading}
        disabled={payLoading}
        startDecorator={<PaymentIcon sx={{ fontSize: 16 }} />}
        sx={compactPayBtnSx}
      >
        {t('customer.pay')}
      </MenuButton>
      <Menu placement="bottom-end" size="sm">
        {payable.map((checkout) => {
          const checkoutTitle = getCheckoutTitle(checkout, locale, t);
          const itemLabel = (
            <>
              {checkoutTitle}
              {' — '}
              <OmrAmount value={checkout.amount} />
            </>
          );
          return (
            <MenuItem
              key={checkout.id}
              disabled={payLoading}
              onClick={() => handlePayCheckout(checkout.id)}
            >
              <ListItemDecorator>
                <PaymentIcon fontSize="small" />
              </ListItemDecorator>
              {itemLabel}
            </MenuItem>
          );
        })}
        {showPayFullRemaining ? (
          <MenuItem onClick={() => setPayFullOpen(true)}>
            <ListItemDecorator>
              <OpenInNewIcon fontSize="small" />
            </ListItemDecorator>
            {t('customer.payFullRemaining')}
            {invoice.amount_due != null ? (
              <>
                {' '}(<OmrAmount value={invoice.amount_due} />)
              </>
            ) : null}
          </MenuItem>
        ) : null}
      </Menu>
    </Dropdown>
  );

  const confirmModal = (
    <Modal open={payFullOpen} onClose={() => !payFullLoading && setPayFullOpen(false)}>
      <ModalDialog variant="outlined" role="alertdialog">
        <DialogTitle>
          <WarningRoundedIcon />
          {t('customer.payFullRemaining')}
        </DialogTitle>
        <DialogContent>{t('customer.payFullConfirmBody')}</DialogContent>
        <DialogActions>
          <Button variant="plain" disabled={payFullLoading} onClick={() => setPayFullOpen(false)}>
            {t('actions.cancel')}
          </Button>
          <Button loading={payFullLoading} onClick={handlePayFull}>
            {t('customer.payFullConfirm')}
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );

  const errorAlert = error ? (
    <Alert color="danger" variant="soft" sx={{ mb: 1 }} onClose={() => setError('')}>
      {error}
    </Alert>
  ) : null;

  const iconActionsSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    flexWrap: 'nowrap',
  };

  const payCheckoutLoading = payLoading || regenerating;

  // Multiple pending checkouts: one Pay button each + pay full
  if (payable.length > 1) {
    if (iconOnly) {
      return (
        <Box sx={iconActionsSx}>
          {errorAlert}
          {renderPayMenu()}
          {confirmModal}
        </Box>
      );
    }
    return (
      <Box sx={{ width: fullWidth ? '100%' : undefined }}>
        {errorAlert}
        <Stack
          direction={compact ? 'row' : 'column'}
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ width: fullWidth ? '100%' : undefined }}
        >
          {payable.map((checkout) => (
            <Button
              key={checkout.id}
              variant={variant}
              color="primary"
              size={size}
              fullWidth={fullWidth && !compact}
              loading={payCheckoutLoading}
              disabled={payCheckoutLoading}
              startDecorator={showStartDecorator ? <PaymentIcon /> : undefined}
              endDecorator={!showStartDecorator ? <OpenInNewIcon sx={{ fontSize: 16 }} /> : undefined}
              onClick={() => handlePayCheckout(checkout.id)}
              sx={{ borderRadius: compact ? '8px' : '10px', fontWeight: 500 }}
            >
              {getCheckoutTitle(checkout, locale, t)}
              {' — '}
              {t('customer.payAmount')} <OmrAmount value={checkout.amount} />
            </Button>
          ))}
          {payFullButton}
        </Stack>
        {confirmModal}
      </Box>
    );
  }

  // Single checkout / legacy link / regenerate — always mint a fresh link on Pay
  if (showSinglePay) {
    const onSinglePay = () => handlePayCheckout(singleCheckoutId);
    if (iconOnly) {
      return (
        <Box sx={iconActionsSx}>
          {errorAlert}
          {renderCompactPayButton(onSinglePay, { loading: payCheckoutLoading, disabled: payCheckoutLoading })}
          {confirmModal}
        </Box>
      );
    }
    return (
      <Box sx={{ width: fullWidth ? '100%' : undefined }}>
        {errorAlert}
        <Stack
          direction={compact ? 'row' : (showPayFullRemaining && payable.length === 1 ? 'column' : 'row')}
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ width: fullWidth ? '100%' : undefined }}
        >
          {renderPayButton(onSinglePay, { loading: payCheckoutLoading, disabled: payCheckoutLoading })}
          {!iconOnly && showPayFullRemaining && payable.length === 1 ? payFullButton : null}
        </Stack>
        {confirmModal}
      </Box>
    );
  }

  // Unpaid with due balance but no checkout/link — offer pay-full only
  if (showPayFullRemaining) {
    return (
      <Box sx={iconOnly ? iconActionsSx : { width: fullWidth ? '100%' : undefined }}>
        {errorAlert}
        {payFullButton}
        {confirmModal}
      </Box>
    );
  }

  // Fallback for callers that still wire onRegenerate
  if (mustRegenerate && typeof onRegenerate === 'function') {
    if (iconOnly) {
      return (
        <Box sx={iconActionsSx}>
          {errorAlert}
          {renderCompactPayButton(onRegenerate, { loading: regenerating, disabled: regenerating })}
          {confirmModal}
        </Box>
      );
    }
    return (
      <Box sx={{ width: fullWidth ? '100%' : undefined }}>
        {errorAlert}
        {renderPayButton(onRegenerate, { loading: regenerating, disabled: regenerating })}
        {confirmModal}
      </Box>
    );
  }

  return null;
}
